/**
 * SubscriptionManagement - Subscription Management Dashboard
 * Migrated from src/app/components/admin/SubscriptionManagement.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import AdminCache from '../components/shared/AdminCache';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import DataTable from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import SearchBar from '../components/common/SearchBar';
import { API_ENDPOINTS } from '../config/api-endpoints';

const { TableSkeleton } = SkeletonLoaderComponents;

const SubscriptionManagement = () => {
  const { user: currentUser } = useAdminAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);

  const isSuperAdmin = currentUser?.isSuperAdmin || false;

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.SUBSCRIPTION}/admin/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Subscription fetch failed: ${response.status}`);
      }
      const data = await response.json();
      setSubscriptions(data.subscriptions || []);
      calculateMetrics(data.subscriptions || []);
    } catch (err) {
      console.error('[SubscriptionManagement] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const calculateMetrics = (subs) => {
    const active = subs.filter(s => s.status === 'active').length;
    const planPrices = { premium: 9.99, family: 19.99, free: 0 };
    const mrr = subs.filter(s => s.status === 'active')
      .reduce((sum, s) => sum + (planPrices[s.plan] || 0), 0);
    
    setMetrics({
      total: subs.length,
      active,
      mrr: mrr.toFixed(2)
    });
  };

  const columns = [
    {
      key: 'user',
      label: 'User',
      render: (sub) => (
        <div>
          <div>{sub.user?.name || 'Unknown'}</div>
          <div className="admin-data-table__cell--secondary">{sub.user?.email || sub.userId}</div>
        </div>
      )
    },
    {
      key: 'plan',
      label: 'Plan',
      render: (sub) => <StatusBadge text={sub.plan} variant="info" />
    },
    {
      key: 'status',
      label: 'Status',
      render: (sub) => {
        const variant = sub.status === 'active' ? 'success' : 
                       sub.status === 'cancelled' ? 'danger' : 'warning';
        return <StatusBadge text={sub.status} variant={variant} />;
      }
    },
    {
      key: 'dates',
      label: 'Period',
      render: (sub) => (
        <div>
          <div>{new Date(sub.startDate).toLocaleDateString()}</div>
          <div className="admin-data-table__cell--secondary">
            to {sub.endDate ? new Date(sub.endDate).toLocaleDateString() : 'N/A'}
          </div>
        </div>
      )
    }
  ];

  if (loading) {
    return <TableSkeleton rows={8} columns={4} />;
  }

  return (
    <div className="subscription-management">
      <div className="subscription-management__header">
        <h2>Subscription Management</h2>
        {isSuperAdmin && <span className="subscription-management__super-badge">🔐 SuperAdmin</span>}
      </div>

      {error && (
        <div className="admin-alert admin-alert--warning">
          <span>⚠️</span> {error}
        </div>
      )}

      {metrics && (
        <div className="admin-grid admin-grid-3">
          <div className="admin-stat-card">
            <div className="admin-stat-icon purple">📊</div>
            <div className="admin-stat-value">{metrics.total}</div>
            <div className="admin-stat-label">Total Subscriptions</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon green">✅</div>
            <div className="admin-stat-value">{metrics.active}</div>
            <div className="admin-stat-label">Active</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon blue">💰</div>
            <div className="admin-stat-value">${metrics.mrr}</div>
            <div className="admin-stat-label">Monthly Revenue</div>
          </div>
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Subscriptions ({subscriptions.length})</h3>
        </div>
        <DataTable
          columns={columns}
          data={subscriptions}
          loading={loading}
          emptyMessage="No subscriptions found"
        />
      </div>
    </div>
  );
};

export default SubscriptionManagement;
